import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { LabellingCoverageGranularity } from "store/api/generatedApi";
import dayjs from "dayjs";
import { useState, useMemo } from "react";
import { TenantCoverageCard } from "./TenantCoverageCard";
import { sorted } from "helpers";
import { CoverageControls } from "./CoverageControls";
import { useAvailableTenantMetadataContext } from "contexts/availableTenantMetadata";

export const LabellingCoverageOverviewPage = () => {
  const { t } = useTenantTranslation();
  const [gapTolerance, setGapTolerance] = useState(60);
  const [granularity, setGranularity] =
    useState<LabellingCoverageGranularity>("hourly");

  const endDate = dayjs().endOf("day").toISOString();
  const startDate = dayjs().subtract(2, "days").startOf("day").toISOString();

  const { tenantMetadataList, isLoading } = useAvailableTenantMetadataContext();
  const filteredTenants = useMemo(
    () =>
      sorted(
        tenantMetadataList.filter((t) =>
          t.feature_flags.includes("scrap_eyes")
        ),
        (t) => t.tenant_name
      ),
    [tenantMetadataList]
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="h4" sx={{ flex: 1 }}>
          {t("labellingCoverageOverview")}
        </Typography>
        <CoverageControls
          granularity={granularity}
          onGranularityChange={setGranularity}
          gapTolerance={gapTolerance}
          onGapToleranceChange={setGapTolerance}
        />
      </Stack>

      <Stack spacing={2}>
        {filteredTenants.map((tenant) => (
          <TenantCoverageCard
            key={tenant.tenant_name}
            tenantName={tenant.tenant_name}
            startDate={startDate}
            endDate={endDate}
            granularity={granularity}
            gapTolerance={gapTolerance}
          />
        ))}
      </Stack>

      {filteredTenants.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="text.secondary">{t("noTenantsFound")}</Typography>
        </Box>
      )}
    </Stack>
  );
};
